import { parseLanguages } from 'core/lite/parsers';
import { compareField } from 'core/utils/utils';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { put, takeLatest } from 'redux-saga/effects';
import { getLangs, getLangScopes } from '../crud/langs.crud';
import { PERSIST_LANGS, REDUCER_LANGS } from './conf';

export const actionTypes = {
  GetLangs: 'cms/GET_LANGS',
  SetLangs: 'cms/SET_LANGS',
  GetLangScopes: 'cms/GET_LANG_SCOPES',
  SetLangScopes: 'cms/SET_LANG_SCOPES',
};

// Nota canónica sobre la (no) persistencia de este duck — referenciada desde el hook y los tests:
// el `whitelist: ['data']` del persistReducer apunta a una clave que NO existe en este estado
// (las claves son `langs`, `scopes`, `loading` y `loaded`), así que redux-persist no persiste
// nada. Tras cada recarga `langs` arranca vacío y hay que esperar la respuesta de `GET /langs`
// antes de decidir nada sobre la lista: `langs: []` no significa "esta editorial tiene un solo
// idioma", puede significar "aún no hay respuesta". De ahí el flag `loaded`.
const initialState = {
  langs: [],
  scopes: [],
  loading: false,
  loaded: false,
};

// Referencia estable para los selectores: devolver un array nuevo por llamada rompería la
// comparación por referencia de react-redux y re-renderizaría a los consumidores en cada acción.
const EMPTY_LANGS = [];

export const reducer = persistReducer({ storage, key: PERSIST_LANGS, whitelist: ['data'] }, (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.GetLangs:
      return { ...state, loading: true };

    case actionTypes.SetLangs: {
      const { response, isFilter } = action.payload;

      if (response && response.status === 'success' && response.data) {
        const langs = parseLanguages(response.data, isFilter).sort((a, b) => compareField(a, b, 'description'));
        return { ...state, langs, loading: false, loaded: true };
      }

      // La petición terminó (aunque fuese en error): `loaded` marca que ya hay respuesta
      // y `langs` se queda como estaba.
      return { ...state, loading: false, loaded: true };
    }

    case actionTypes.SetLangScopes: {
      const data = action.payload?.data ? action.payload.data : [];
      return { ...state, scopes: data };
    }

    default:
      return state;
  }
});

export const selectors = {
  getLangs: (state) => state?.entities?.[REDUCER_LANGS]?.langs ?? EMPTY_LANGS,
  getLangScopes: (state) => {
    return state.entities && state.entities[REDUCER_LANGS] ? state.entities[REDUCER_LANGS].scopes : null;
  },
  // true en cuanto ha llegado ALGUNA respuesta de `GET /langs`, con éxito o con error, y se
  // mantiene true en refetches posteriores. Es lo que distingue "todavía no sabemos qué idiomas
  // hay" de "ya sabemos que hay 0 o 1" (ver la nota de initialState). A propósito NO se expone
  // `loading`: la petición se relanza varias veces por sesión (ver deps del efecto en
  // `src/app/router/Routes.js`) y un gate basado en él parpadearía en cada refetch.
  getIsLangsLoaded: (state) => state?.entities?.[REDUCER_LANGS]?.loaded ?? false,
};

export const actions = {
  getLangs: (params) => ({ type: actionTypes.GetLangs, payload: { ...params } }),
  fulfillLangs: (response, isFilter) => ({ type: actionTypes.SetLangs, payload: { response, isFilter } }),
  getLangScopes: (params) => ({ type: actionTypes.GetLangScopes, payload: params }),
  fulfillLangScopes: (response) => ({ type: actionTypes.SetLangScopes, payload: response }),
};

export function* saga() {
  yield takeLatest(actionTypes.GetLangs, function* getLangsSaga(action) {
    // El try/catch garantiza que SIEMPRE se despacha `SetLangs` (y por tanto `loaded: true`):
    // la petición puede lanzar (p. ej. un error inesperado en un interceptor de axios) y sin
    // captura la pestaña quedaría oculta para siempre y el throw tumbaría el rootSaga. El try abarca SOLO la petición:
    // una excepción del reducer no debe tragarse como "respuesta vacía" (review PR #1094).
    let response;
    try {
      response = yield getLangs();
    } catch (error) {
      console.error('getLangsSaga: GET /langs failed', error);
      response = undefined;
    }
    yield put(actions.fulfillLangs(response?.data, action.payload.isFilter));
  });

  yield takeLatest(actionTypes.GetLangScopes, function* getLangScopesSaga() {
    const { data } = yield getLangScopes();
    yield put(actions.fulfillLangScopes(data));
  });
}
